import { v4 as uuidv4 } from 'uuid'

import { SessionConfig, TokenProvider } from '../core/ports'
import { ChatSession } from '../core/stores/Session'
import { HttpChatApi } from '../core/transport/api'
import { SocketIoChatSocket } from '../core/transport/socketIo'

export interface ChatSessionDeps {
  readonly userId: string
  readonly getToken: TokenProvider
  readonly onReadStateChanged?: (channelId?: string) => void
}

let current: { session: ChatSession; key: string } = null

// The web app is served behind the same reverse proxy that fronts the chat
// service, so the page origin is the chat host — no per-tenant resolution
// and no host header override, unlike the native composition.
export function acquireChatSession(deps: ChatSessionDeps): ChatSession {
  const host = window.location.origin
  const key = `${deps.userId}|${host}`

  if (current?.key === key) {
    return current.session
  }

  current?.session.dispose()

  const logger = {
    warn: (message: string) => console.warn(message),
    error: (message: string) => console.error(message),
  }

  const config: SessionConfig = {
    userId: deps.userId,
    host,
    getToken: deps.getToken,
    clock: () => Date.now(),
    newId: () => uuidv4(),
    logger,
    onReadStateChanged: deps.onReadStateChanged,
  }

  const api = new HttpChatApi({
    host,
    getToken: deps.getToken,
    onDecodeErrors: (route, errors) =>
      logger.warn(
        `chat decode ${route}: ${errors.map(e => e.detail).join('; ')}`,
      ),
  })

  const socket = new SocketIoChatSocket({
    host,
    userId: deps.userId,
    getToken: deps.getToken,
    logger,
  })

  const session = new ChatSession(config, api, socket)
  session.start()
  current = { session, key }

  return session
}

/**
 * Tears down the cached session and its transport. Called on logout, before
 * the full-page redirect to the login bundle, and between composition tests.
 * @public
 */
export function disposeChatSession(): void {
  current?.session.dispose()
  current = null
}
